import { DateTime } from "luxon";

import type { OpenTask } from "@storyboard/dispatch-view/task";
import type { DispatchGrain, DispatchViewDefinition } from "@storyboard/dispatch-view/view";
import type { TaskSubjectWrite } from "@storyboard/web-client";
import type { DriverRow } from "@/types/driver";
import type { DispatchTableRow } from "./dispatchTableRow";
import {
  type ItemTarget,
  noteRequiredFor,
  openTaskOfTarget,
  type TaskAbout,
  type TaskFold,
  taskSubjectOfRow,
} from "./tasks";

/** One board the task picker's dropdown offers: a view, the viewer's own labeled as theirs. */
export type TaskViewOption = { id: string; label: string };

/**
 * The boards the task picker's dropdown lists: every view the workspace offers, the built-in default included, the
 * viewer's own first and labeled "(your board)".
 *
 * The rest keep the order they are given in.
 */
export function taskViewOptionsFor({
  views,
  currentViewId,
}: {
  views: readonly DispatchViewDefinition[];
  currentViewId: string;
}): TaskViewOption[] {
  const own = views.filter((view) => view.id === currentViewId);
  const rest = views.filter((view) => view.id !== currentViewId);
  return [
    ...own.map((view) => ({ id: view.id, label: `${view.label} (your board)` })),
    ...rest.map((view) => ({ id: view.id, label: view.label })),
  ];
}

/** One answer to what a new task is about, as the picker's About switch offers it. */
export type TaskAboutChoice = {
  about: TaskAbout;
  /** What the switch reads: "This message", "This event", "The driver only", "Each item", or "Each driver only". */
  label: string;
  /** Whether the task must carry a note: one about a driver with nothing waiting says nothing on its own. */
  requiresNote: boolean;
  /**
   * The open task the one message or event already carries, which Submit moves rather than doubles; always null for a
   * driver, who can carry many tasks, and for several rows.
   */
  current: OpenTask | null;
};

/** The driver a History entry's task can be about instead of the entry: the driver the panel is open on. */
export type TaskDriver = {
  workspaceMembershipId: string;
  /** The driver's name, as the picker's About header reads it. */
  label: string;
  /** How many things wait on the driver's channel; none means a task about the driver needs a note. */
  waiting: number;
};

/** The name a driver goes by in the picker: their name, else their alias. */
export function taskDriverLabel(driver: DriverRow): string {
  return driver.name ?? driver.alias ?? "Driver";
}

/** How many drivers the About header names before it counts them instead. */
const ABOUT_NAMES_SHOWN = 3;

/**
 * The drivers a task made from table rows is about, as the picker's About header names them: each row's driver once,
 * in row order.
 *
 * @param rows - The rows the task is made from.
 */
export function rowTaskDriverNames(rows: readonly DispatchTableRow[]): string[] {
  const byMembership = new Map(rows.map(({ driver }) => [driver.workspaceMembershipId, taskDriverLabel(driver)]));
  return [...byMembership.values()];
}

/** The picker's About header: the drivers' names, comma-separated, or {@link driverCountLabel} past a few of them. */
export function taskAboutHeading(names: readonly string[]): string {
  return names.length > ABOUT_NAMES_SHOWN ? driverCountLabel(names.length) : names.join(", ");
}

/** "N drivers": what the About header reads for several drivers whose names would not fit on its line. */
export function driverCountLabel(count: number): string {
  return count === 1 ? "1 driver" : `${count} drivers`;
}

/** What the switch reads for one message or event: a `feed_item` is a message, every other subject an event. */
function itemLabelOf(target: TaskSubjectWrite): string {
  return target.subject === "feed_item" ? "This message" : "This event";
}

/**
 * What a task made from table rows can be about, the default first: the message or event, then the driver.
 *
 * While the workspace keeps tasks about a message or event off, at the driver grain, and for rows with no message or
 * event a task can name, the only choice is the driver, and the picker shows no switch. Several rows read "Each item"
 * and "Each driver only".
 *
 * @param rows - The rows the task is made from.
 * @param grain - The view's grain.
 * @param fold - The workspace's open tasks, to find the one already on a lone message or event.
 * @param unacknowledgedByFeed - How many things wait on each channel, to decide whether a driver's task needs a note.
 * @param itemTasks - Whether the workspace lets a new task be about a message or event.
 */
export function rowTaskAboutChoices(
  rows: readonly DispatchTableRow[],
  grain: DispatchGrain,
  fold: TaskFold,
  unacknowledgedByFeed: ReadonlyMap<string, number>,
  itemTasks: boolean,
): TaskAboutChoice[] {
  const [lone] = rows.length === 1 ? rows : [];
  const driver: TaskAboutChoice = {
    about: "driver",
    label: lone ? "The driver only" : "Each driver only",
    requiresNote: noteRequiredFor(rows, "driver", unacknowledgedByFeed),
    current: null,
  };
  if (!itemTasks || grain === "driver") return [driver];
  // At the message grain, a row offers its message or event when a task can name one other than the driver.
  const items = rows.flatMap((row) => {
    const subject = taskSubjectOfRow(row, grain);
    return subject === null || subject.subject === "membership" ? [] : [subject];
  });
  const [loneItem] = items;
  if (loneItem === undefined) return [driver];
  const item: TaskAboutChoice = {
    about: "item",
    label: lone ? itemLabelOf(loneItem) : "Each item",
    requiresNote: noteRequiredFor(rows, grain, unacknowledgedByFeed),
    current: lone ? openTaskOfTarget(loneItem, fold) : null,
  };
  return [item, driver];
}

/**
 * What a task made from a History entry can be about, the default first: the message or event, then the driver the
 * panel is open on. An entry no task can name offers only the driver, as does every entry while the workspace keeps
 * tasks about a message or event off; a panel on no driver offers only the entry.
 *
 * @param target - The message or event, or null for an event of a subject no task names.
 * @param driver - The driver the History panel is open on, or null when it is open on none.
 * @param fold - The workspace's open tasks, to find the one already on the message or event.
 * @param itemTasks - Whether the workspace lets a new task be about a message or event.
 */
export function entryTaskAboutChoices(
  target: ItemTarget | null,
  driver: TaskDriver | null,
  fold: TaskFold,
  itemTasks: boolean,
): TaskAboutChoice[] {
  const choices: TaskAboutChoice[] = [];
  if (target && itemTasks) {
    choices.push({
      about: "item",
      label: itemLabelOf(target),
      requiresNote: false,
      current: openTaskOfTarget(target, fold),
    });
  }
  if (driver) {
    choices.push({ about: "driver", label: "The driver only", requiresNote: driver.waiting === 0, current: null });
  }
  return choices;
}

/**
 * What a task made from a History entry names: the entry itself, or the driver the panel is open on; null when the
 * chosen one is missing.
 *
 * @param target - The message or event, or null for an event of a subject no task names.
 * @param driverMembershipId - The driver the History panel is open on, or null.
 * @param about - What the dispatcher chose the task to be about.
 */
export function entryTaskSubject(
  target: ItemTarget | null,
  driverMembershipId: string | null,
  about: TaskAbout,
): TaskSubjectWrite | null {
  if (about === "item") return target;
  return driverMembershipId === null ? null : { subject: "membership", targetId: driverMembershipId };
}

/**
 * The muted line under the board when the chosen message or event already carries an open task: Submit moves that
 * task rather than making a second.
 *
 * @param current - The open task already on the subject.
 * @param viewLabelFor - A board's label by view id; null for a view the workspace no longer has.
 */
export function existingTaskLine(current: OpenTask, viewLabelFor: (viewId: string) => string | null): string {
  const board = current.pin ? viewLabelFor(current.pin.viewId) : null;
  return board ? `Already a task on ${board}. Submit moves it.` : "Already a task. Submit moves it.";
}

/**
 * How the picker says a task clears: after acknowledgement, after a preset or a typed time, or, for a task that has one, at
 * the expiry already in force.
 */
export type TaskExpiryChoice = "acknowledged" | "hour" | "fourHours" | "endOfDay" | "custom" | "current";

/** The picker's choices, in the order it lists them, "Acknowledgement" first as a new task's default. */
export const TASK_EXPIRY_OPTIONS: readonly { value: Exclude<TaskExpiryChoice, "current">; label: string }[] = [
  { value: "acknowledged", label: "Acknowledgement" },
  { value: "hour", label: "1 hour" },
  { value: "fourHours", label: "4 hours" },
  { value: "endOfDay", label: "End of today" },
  { value: "custom", label: "A set time…" },
];

/** Whether a dropdown value is one of the picker's choices. */
export function isTaskExpiryChoice(value: string): value is TaskExpiryChoice {
  return value === "current" || TASK_EXPIRY_OPTIONS.some((option) => option.value === value);
}

/**
 * The expiry as the picker holds it: the choice, the custom field's `yyyy-MM-ddTHH:mm` in the viewer's zone, and the
 * expiry in force on the task a move would pin, null for none.
 */
export type TaskExpiryInput = { choice: TaskExpiryChoice; custom: string; current?: string | null };

/**
 * The instant a choice names, as UTC ISO; null for "Acknowledgement", and for "current" on a task with none;
 * undefined for a custom time that is empty, does not parse, or is not after `now`, which Submit refuses.
 *
 * @param expiry - The choice, the custom field's value, and the expiry in force.
 * @param now - The server's clock as the page estimates it, as epoch milliseconds; "End of today" is the end of its day
 *   in the viewer's zone.
 */
export function taskExpiryFor({ choice, custom, current }: TaskExpiryInput, now: number): string | null | undefined {
  const clock = DateTime.fromMillis(now);
  switch (choice) {
    case "acknowledged":
      return null;
    case "current":
      // An expiry that has passed names a task already gone; keeping it would pin the task again expired.
      if (current && hasPassed(current, now)) return undefined;
      return current ?? null;
    case "hour":
      return clock.plus({ hours: 1 }).toUTC().toISO() ?? undefined;
    case "fourHours":
      return clock.plus({ hours: 4 }).toUTC().toISO() ?? undefined;
    case "endOfDay":
      return clock.endOf("day").toUTC().toISO() ?? undefined;
    case "custom": {
      const at = DateTime.fromISO(custom);
      return at.isValid && at.toMillis() > now ? (at.toUTC().toISO() ?? undefined) : undefined;
    }
  }
}

/**
 * Whether an expiry is at or before `now`, as epoch milliseconds.
 */
export function hasPassed(expiresAt: string, now: number): boolean {
  return DateTime.fromISO(expiresAt).toMillis() <= now;
}

/**
 * The hint under the picker, which says what the choice does: a task that clears when acknowledged, or one that
 * stays pinned until its time whatever is acknowledged. Null while a typed time is not yet a time after now.
 *
 * @param expiresAt - What {@link taskExpiryFor} gave for the choice.
 * @param formatTime - How the page shows an instant.
 */
export function taskExpiryHint(
  expiresAt: string | null | undefined,
  formatTime: (iso: string) => string | null,
): string | null {
  if (expiresAt === undefined) return null;
  if (expiresAt === null) return "Stays pinned until someone acknowledges it.";
  const time = formatTime(expiresAt);
  return time
    ? `Stays pinned until ${time}, whether or not anyone acknowledges it.`
    : "Stays pinned until its time, whether or not anyone acknowledges it.";
}

/**
 * What submitting the picker does: make a task about the chosen subject on a board, or pin an open one there with the
 * expiry chosen, which moves it, changes its expiry, or both.
 */
export type TaskSubmission =
  | { kind: "make"; about: TaskAbout; viewId: string; note: string | null; expiresAt: string | null }
  | { kind: "move"; viewId: string; expiresAt: string | null };

/**
 * What Submit and Enter do now, or null while they do nothing: no board is chosen, a required note is empty, a typed
 * expiry is not a time after now, or a move would change neither the task's board nor its expiry.
 */
export function taskSubmissionFor(
  input:
    | {
        mode: "make";
        about: TaskAbout;
        viewId: string | null;
        note: string;
        requiresNote: boolean;
        /** The expiry chosen and the clock it is read against. */
        expiry: TaskExpiryInput & { now: number };
      }
    | {
        mode: "move";
        viewId: string | null;
        pinnedViewId: string | null;
        /** The expiry chosen, with the one in force as `current`, and the clock it is read against. */
        expiry: TaskExpiryInput & { now: number };
      },
): TaskSubmission | null {
  if (input.viewId === null) return null;
  const expiresAt = taskExpiryFor(input.expiry, input.expiry.now);
  if (expiresAt === undefined) return null;
  if (input.mode === "move") {
    // Any pin brings an expired task back, so for one the only refusal is a choice that is not a time.
    const current = input.expiry.current ?? null;
    const expired = current !== null && hasPassed(current, input.expiry.now);
    const unchanged = !expired && input.viewId === input.pinnedViewId && expiresAt === current;
    return unchanged ? null : { kind: "move", viewId: input.viewId, expiresAt };
  }
  const trimmed = input.note.trim();
  if (input.requiresNote && trimmed === "") return null;
  return { kind: "make", about: input.about, viewId: input.viewId, note: trimmed === "" ? null : trimmed, expiresAt };
}
